import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Select,
  Space,
  Popconfirm,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  HomeOutlined,
  DeleteOutlined,
  EditOutlined,
  UserOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  getCustomers,
  deleteCustomer,
  getCities,
  getRegions,
} from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../context/AppContextProvider";
import { useNavigate } from "react-router-dom";
import {
  AddonName,
  UserType,
} from "../../../configs/constants";
import AddCustomer from "./AddCustomer";
import CustomerFormModal from "./CustomerFormModal";
import { handleApiError } from "../../../utils/errorHandler";
import { getTableRowNumber } from "../../../utils/tablePagination";

const Customers = () => {
  const { authUser, appConfig } = useContext(AppContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Table state
  const [tableData, setTableData] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [paginationData, setPaginationData] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(10);

  // Search state
  const [searchParams, setSearchParams] = useState([]);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [searchFormRef] = Form.useForm();
  const selectedRegionId = Form.useWatch("region_id", searchFormRef);

  const [editingCustomer, setEditingCustomer] = useState(null);

  const is_customer_management_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.CustomerManagement,
  );

  const canAddEditCustomer =
    is_customer_management_active &&
    (authUser.type === UserType.BusinessOwner ||
      authUser.can_add_edit_customer);

  useEffect(() => {
    setIsDataLoading(true);
    getCustomers({
      ...searchParams,
      page: currentPage,
      per_page: perPageSize,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setTableData(response.data.data.customers);
        setPaginationData(response.data.data.pagination);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reloadTableData, searchParams, currentPage, perPageSize, t]);

  useEffect(() => {
    getRegions()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setRegions(response.data.data.regions);
      })
      .catch((error) => handleApiError(error, t));
  }, [t]);

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
  };

  const handleModalClose = () => {
    setEditingCustomer(null);
  };

  const handleModalSuccess = () => {
    setReloadTableData((v) => !v);
  };

  const handleDelete = (id) => {
    deleteCustomer(id)
      .then((response) => {
        if (response.data.success === true) {
          setReloadTableData((v) => !v);
          NotificationWithIcon(
            "success",
            t("customer_deleted"),
            t("customer_deleted_successfully"),
          );
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  const handleRegionSelectForFilter = (value) => {
    searchFormRef.resetFields(["city_id"]);
    setCities([]);

    if (!value) {
      setCitiesLoading(false);
      return;
    }

    setCitiesLoading(true);
    getCities(value)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setCities(response.data.data.cities);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setCitiesLoading(false);
      });
  };

  const handleSearch = (values) => {
    const params = {};
    if (values.user_type) params.user_type = values.user_type;
    if (values.phone) params.phone = values.phone;
    if (values.region_id) params.region_id = values.region_id;
    if (values.city_id) params.city_id = values.city_id;

    setSearchParams(Object.keys(params).length ? params : []);
  };

  const handleReset = () => {
    searchFormRef.resetFields();
    setCities([]);
    setCitiesLoading(false);
    setSearchParams([]);
  };

  const handleTableChange = (pagination) => {
    setPaginationData(pagination);
    setCurrentPage(pagination.current);
    setPerPageSize(pagination.pageSize);
  };

  const breadcrumbRoutes = [
    { path: "/", breadcrumbName: t("home"), icon: <HomeOutlined /> },
    {
      path: "customers",
      breadcrumbName: t("customers"),
      icon: <UserOutlined />,
    },
  ];

  const filterByLabel = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const localizedLabel = (item) =>
    appConfig.direction === "ltr" ? item.name_en : item.name_ar;

  const TableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => getTableRowNumber(index, paginationData),
    },
    { title: t("name_ar"), render: (record) => record.name_ar },
    { title: t("name_en"), render: (record) => record.name_en || "-" },
    { title: t("phone_number"), render: (record) => record.phone || "-" },
    { title: t("type"), render: (record) => record.user_type || "-" },
    {
      title: t("action"),
      align: "center",
      width: 150,
      render: (record) => (
        <Space>
          <Button
            type="link"
            key="View"
            title={t("view_details")}
            onClick={() => navigate("/customers/" + record.id)}
          >
            <EyeOutlined />
          </Button>
          {canAddEditCustomer && (
            <Button
              type="link"
              key="edit"
              onClick={() => openEditModal(record)}
              title={t("customer_edit")}
            >
              <EditOutlined />
            </Button>
          )}

          {canAddEditCustomer && (
            <Popconfirm
              title={t("warning_delete")}
              onConfirm={() => handleDelete(record.id)}
              okText={t("yes")}
              cancelText={t("no")}
              placement="topLeft"
            >
              <Button type="link" title={t("delete_entity")}>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const searchForm = (
    <Form
      name="search-form"
      layout="inline"
      onFinish={handleSearch}
      form={searchFormRef}
    >
      <Form.Item name="user_type" label={t("type")}>
        <Select
          allowClear
          style={{ width: "120px" }}
          options={[
            { value: "individual", label: t("individual") },
            { value: "company", label: t("company") },
          ]}
        />
      </Form.Item>
      <Form.Item name="phone" label={t("phone_number")}>
        <Input style={{ width: "150px" }} />
      </Form.Item>
      <Form.Item name="region_id" label={t("region")}>
        <Select
          allowClear
          style={{ width: "150px" }}
          showSearch
          onChange={handleRegionSelectForFilter}
          options={regions.map((region) => ({
            label: localizedLabel(region),
            value: region.id,
          }))}
          filterOption={filterByLabel}
        />
      </Form.Item>
      <Form.Item name="city_id" label={t("city")}>
        <Select
          allowClear
          style={{ width: "150px" }}
          showSearch
          loading={citiesLoading}
          disabled={!selectedRegionId || citiesLoading}
          options={cities.map((city) => ({
            label: localizedLabel(city),
            value: city.id,
          }))}
          filterOption={filterByLabel}
        />
      </Form.Item>
      <Form.Item>
        <Input
          type="submit"
          value={t("search")}
          className="app-search-submit"
        />
      </Form.Item>
      <Form.Item>
        <Input type="button" value={t("reset")} onClick={handleReset} />
      </Form.Item>
    </Form>
  );

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          title={t("customers")}
          ghost={false}
          className="app-p-0"
          footer={searchForm}
          extra={
            canAddEditCustomer && (
              <AddCustomer
                iconOnly={false}
                reloadCallback={handleModalSuccess}
              />
            )
          }
        />
      </Card>

      <Card>
        <Table
          columns={TableColumns}
          dataSource={tableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={{
            current: paginationData.current_page,
            pageSize: paginationData.per_page,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </Card>

      <CustomerFormModal
        open={Boolean(editingCustomer)}
        mode="edit"
        initialValues={editingCustomer}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </>
  );
};

export default Customers;
